import React, {useState, useEffect, useRef, useCallback, useContext} from "react";
import {useParams} from "react-router-dom";
import {FormProvider, useForm} from "react-hook-form";
import {PowerBIEmbed} from "powerbi-client-react";
import {models} from "powerbi-client";
import styled from "styled-components";
import {yupResolver} from "@hookform/resolvers/yup";
import * as yup from "yup";

// Hooks
import useMountedState from "../../hooks/useMountedState.js";
import useApi from "../../hooks/useApi.js";

// Utils
import {CHECKSHEET, EVENT} from "../../utils/report.js";
import {getFields, mergeBuilders} from "../../utils/builder.js";

// Contexts
import {FacilityNavContext} from "../../contexts/facilitynav.js";

// Components
import Dropdown from "../../components/Dropdown.js";
import Region from "./Region.js";
import {InputCheckGroup, InputRadioGroup, InputSelect} from "../../components/form/FormInputs.js";

// Style
import {bp} from "../../style/components/breakpoints.js";
import {flex} from "../../style/components/mixins.js";
import {border, pad, radius} from "../../style/components/variables.js";
import {
  Button,
  Form,
  FormField,
  FormGroup,
  Label,
  Loader,
  Page,
  Text,
  Title
} from "../../style/components/general.js";

const REPORT_WIDTH = 900;

const DATA_TYPES = [CHECKSHEET, EVENT];

const defaultValues = {
  action: "report"
};

const schema = yup.object().shape({
  action: yup.string().required(),
  report: yup
    .string()
    .nullable()
    .when("action", {
      is: "report",
      then: () => yup.object().shape({})
    }),
  dataset: yup
    .string()
    .nullable()
    .when("action", {
      is: "dataset",
      then: () =>
        yup.object().shape({
          type: yup.string().required("Please provide a type."),
          source: yup
            .mixed()
            .nullable()
            .when("dataType", {
              is: val => val !== "Chart" && val !== "Statistic",
              then: () => yup.string().required("Please provide a source.")
            }),
          fields: yup.array()
        })
    })
});

const RenderReportPowerBI = () => {
  const isMounted = useMountedState();

  const {slug} = useParams();

  const {facility} = useContext(FacilityNavContext);

  const {api: apiMS} = useApi("microsoft");
  const {api: apiChecksheets} = useApi("checksheets");
  const {api: apiEvents} = useApi("events");
  const {api: apiReports} = useApi("reports");

  const [reports, setReports] = useState(null);
  const [selectedReport, setSelectedReport] = useState(null);
  const [message, setMessage] = useState("No Report selected.");
  // Dataset States
  const [sourceOptions, setSourceOptions] = useState(null);
  const [fieldOptions, setFieldOptions] = useState(null);
  const [selected, setSelected] = useState(null);
  const [preview, setPreview] = useState(null);

  const embedded = useRef();

  const form = useForm({
    defaultValues,
    resolver: yupResolver(schema)
  });
  const {watch, handleSubmit} = form;
  const action = watch("action");
  // Report
  // Dataset
  const dataType = watch("dataset.type");
  const dataSource = watch("dataset.source");
  const dataFields = watch("dataset.fields");

  useEffect(() => {
    if (action === "dataset") setReports(null);
  }, [action]);

  // Get Reports
  useEffect(() => {
    if (isMounted() && action === "report" && reports === null)
      apiMS
        .callGet(null, {resourceType: "powerbi", resourcePath: "reports"})
        .then(({status, data}) => {
          if (status === 200 && data) setReports(data.reports);
        });
  }, [isMounted, action, reports, apiMS]);

  // Dataset
  const getSources = useCallback(
    api =>
      api.callGet(null, {facilitySlug: slug}).then(({status, data}) => {
        if (status === 200 && data)
          setSourceOptions(
            data.map(({id, name, ...rest}) => ({id, value: id, label: name, ...rest}))
          );
      }),
    [slug]
  );

  // Update source options (Availbale Checksheet(s) / Events(s))
  useEffect(() => {
    if (isMounted() && slug && dataType && dataType === CHECKSHEET) getSources(apiChecksheets);
    if (isMounted() && slug && dataType && dataType === EVENT) getSources(apiEvents);
  }, [isMounted, slug, dataType, apiChecksheets, apiEvents, getSources]);

  useEffect(() => {
    if (isMounted() && dataSource && sourceOptions?.length > 0) {
      const target = sourceOptions.filter(({id}) => `${id}` === dataSource)[0];
      if (target) {
        const {allIds, byId} = mergeBuilders(target.builder, facility.builder);
        const numberFields = getFields(allIds, byId)
          .filter(field => field.type === "number" || field.type === "generated")
          .map(({name, ancestry, units}) => ({name, label: ancestry, units}));

        setFieldOptions(numberFields);
        setSelected(target);
      }
    }
  }, [isMounted, dataSource, sourceOptions, facility]);

  const getRecords = useCallback(() => {
    const headers = {date: {label: "Date", index: 0}};

    dataFields.map((key, i) => {
      const {name, label, defaultLabel, units} = selected.builder.byId[key];
      const newLabel = `${label} ${units && `(${units})`}`;
      headers[name] = {
        label: newLabel,
        enteredLabel: label,
        defaultLabel: defaultLabel,
        units,
        index: i + 1
      };
    });

    const params = {
      start: null, // TODO: add
      end: null, // TODO: add
      regions: [
        {
          rowId: "preview",
          title: "Preview",
          type: dataType,
          sourceId: dataSource,
          headers: headers,
          fields: dataFields
        }
      ]
    };
    apiReports.callPost(params).then(({status, data}) => {
      if (status === 200) setPreview(data.preview);
    });
  }, [apiReports, selected, dataFields, dataSource, dataType]);

  useEffect(() => {
    if (dataFields?.length > 0) getRecords();
  }, [dataFields, getRecords]);

  const handleAddRows = (id, type, rows) =>
    apiMS.callPost({
      resourcePath: `datasets/${id}/tables/${type}/rows`,
      resource: {rows}
    });

  const publishDataSet = () => {
    const {type, headers, data: rows} = preview;

    const formatted = rows.map(row => {
      const temp = {};
      Object.keys(row).map(key => {
        if (selected.builder.byId[key]?.type === "number")
          temp[key] = row[key] !== "-" ? Number.parseFloat(row[key]) : null;
        else temp[key] = row[key];
      });
      return temp;
    });

    const params = {
      resourcePath: "datasets",
      resource: {
        name: slug,
        defaultMode: "Push",
        tables: [
          {
            name: type,
            columns: Object.keys(headers).map(key => ({
              name: key,
              dataType: headers[key]?.units ? "Decimal" : "DateTime"
            }))
          }
        ]
      }
    };

    apiMS.callPost(params).then(({status, data}) => {
      if (status === 201) handleAddRows(data.id, type, formatted);
    });
  };

  return (
    <Page hasMenu>
      <Title>Reporting</Title>

      <Wrapper>
        <PageLeft>
          <FormProvider {...form}>
            <Form onSubmit={handleSubmit(publishDataSet)} noValidate>
              <FormField>
                <InputRadioGroup
                  name="action"
                  label="Select option to get started."
                  orient
                  options={[
                    {
                      label: "Request Report from PowerBI",
                      value: "report"
                    },
                    {
                      label: "Generate Table in PowerBI",
                      value: "dataset"
                    }
                  ]}
                  required
                />
              </FormField>

              <FormGroup>
                {action === "report" ? (
                  <div>
                    {reports ? (
                      <FieldWrapper>
                        <Label bold>View Existing Report</Label>
                        {reports?.length > 0 ? (
                          <Dropdown
                            name="report"
                            placeholder="Select Report..."
                            options={reports.map(({id, name: reportName}) => ({
                              value: id,
                              label: reportName
                            }))}
                            selection={selectedReport}
                            setSelection={selection => setSelectedReport(selection)}
                          />
                        ) : (
                          <Text>No Reports Available</Text>
                        )}
                      </FieldWrapper>
                    ) : (
                      <Loader />
                    )}
                  </div>
                ) : (
                  <>
                    <FormField>
                      <InputSelect
                        name="dataset.type"
                        placeholder="Source Type..."
                        options={DATA_TYPES}
                      />
                    </FormField>
                    {dataType !== "" && sourceOptions?.length > 0 ? (
                      <FormField>
                        <InputSelect
                          name="dataset.source"
                          placeholder="Source..."
                          options={sourceOptions}
                        />
                      </FormField>
                    ) : (
                      <Text>No sources available.</Text>
                    )}
                    {dataSource !== "" && fieldOptions?.length > 0 ? (
                      <FormField>
                        <InputCheckGroup
                          name="dataset.fields"
                          placeholder="Field..."
                          options={fieldOptions}
                        />
                      </FormField>
                    ) : (
                      <Text>No Fields available.</Text>
                    )}

                    <Button type="submit">Publish</Button>
                  </>
                )}
              </FormGroup>
            </Form>
          </FormProvider>
        </PageLeft>

        <PageRight>
          {selectedReport ? (
            <PowerBIEmbed
              embedConfig={{
                id: selectedReport,
                type: "report", // Supported types: report, dashboard, tile, visual, qna, paginated report and create
                embedUrl: reports.filter(({id}) => id === selectedReport)[0].embedUrl,
                accessToken: reports.filter(({id}) => id === selectedReport)[0].accessToken,
                tokenType: models.TokenType.Embed, // Use models.TokenType.Aad for SaaS embed
                settings: {
                  pageNavigation: {
                    visible: false
                  },
                  panes: {
                    filters: {
                      expanded: false,
                      visible: false
                    }
                  },
                  background: models.BackgroundType.Transparent
                }
              }}
              eventHandlers={
                new Map([
                  [
                    "loaded",
                    ({target}) => {
                      target.style.height = "100%";
                    }
                  ],
                  [
                    "error",
                    ({detail}) => {
                      // TODO: should go to sentry?
                      setMessage(
                        `We're sorry, something went wrong loading your report. ${detail}`
                      );
                    }
                  ]
                  // ["visualClicked", () => console.log("visual clicked")],
                  // ["pageChanged", event => console.log(event)]
                ])
              }
              cssClassName="embedReport"
              getEmbeddedComponent={({target}) => {
                embedded.current = target;
              }}
            />
          ) : (
            <Text center>{message}</Text>
          )}

          {preview?.data?.length > 0 && <Region id="1" {...preview} />}
        </PageRight>
      </Wrapper>
    </Page>
  );
};

// Style Overrides
const Wrapper = styled.div`
  ${flex("row", "wrap", "start", "stretch")};
  gap: ${pad * 2}px;
`;

const PageLeft = styled.div`
  position: relative;
  width: 100%;
  min-width: min-content;

  ${bp(3)} {
    width: 30%;
    padding: ${pad}px 0 0;
  }
`;

const PageRight = styled.div`
  ${flex("column", "nowrap", "start", "stretch")};
  flex: 1;
  width: 100%;
  min-height: 70vh;
  min-width: 450px;
  max-width: ${REPORT_WIDTH}px;
  position: relative;
  border: ${border} solid ${({theme}) => theme.secondary};
  border-radius: ${radius};
  overflow: hidden;

  ${bp(3)} {
    margin: ${pad * 2}px 0 0;
    width: 70%;
  }
`;

const FieldWrapper = styled.div`
  position: relative;
`;

export default RenderReportPowerBI;
